import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Chip,
  Grid,
  Alert,
  Modal,
  Backdrop,
  Fade,
  IconButton,
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Add as AddIcon,
  CameraAlt as CameraAltIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useInventory } from '../../contexts/InventoryContext';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../utils/api';
import AddItemModal from '../../components/inventory/AddItemModal';
import AiAddItemModal from '../../components/inventory/AiAddItemModal';
import EditItemModal from '../../components/inventory/EditItemModal';
import InventoryStats from '../../components/inventory/InventoryStats';
import InventoryFilters from '../../components/inventory/InventoryFilters';
import InventoryTable from '../../components/inventory/InventoryTable';
import ImportExportButtons from '../../components/common/ImportExportButtons';
import { exportToCSV, downloadCSV, parseCSV, readFileAsText, formatDateForCSV, formatCurrencyForCSV } from '../../utils/csvUtils';

const Inventory = () => {
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(0); // Material-UI pagination is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [addOpen, setAddOpen] = useState(false);
  const [aiAddOpen, setAiAddOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedModalImage, setSelectedModalImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Additional missing state variables
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [importResultsOpen, setImportResultsOpen] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get inventory data from context
  const {
    items: inventory,
    stats,
    loading,
    error,
    loadInventory,
    addItem,
    updateItem,
    setFilters,
    pagination,
    clearError,
  } = useInventory();

  const { selectedLocation } = useLocation();

  // Helper functions for status display
  const getStatusText = (item) => {
    if (!item) return 'Unknown';
    const quantity = item.quantity || item.available || 0;
    const reorderPoint = item.reorderPoint || 0;
    
    if (quantity <= 0) return 'Out of Stock';
    if (quantity <= reorderPoint) return 'Low Stock';
    return 'In Stock';
  };

  const getStatusColor = (item) => {
    if (!item) return 'default';
    const quantity = item.quantity || item.available || 0;
    const reorderPoint = item.reorderPoint || 0;
    
    if (quantity <= 0) return 'error';
    if (quantity <= reorderPoint) return 'warning';
    return 'success';
  };

  // CSV Export Handler - exports ALL inventory from backend
  const handleExport = async () => {
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) {
      alert('Please select a location first');
      return;
    }

    try {
      const response = await api.get(`/inventory/export?locationId=${locationId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `inventory_${selectedLocation?.name || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export inventory');
    }
  };

  // CSV Import Handler - uses backend import endpoint
  const handleImport = async (file) => {
    const locationId = selectedLocation?.id || selectedLocation?._id;
    if (!locationId) {
      alert('Please select a location first');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locationId', locationId);

      const response = await api.post('/inventory/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Show import results modal
        setImportResults(response.data.data);
        setImportResultsOpen(true);
        // Reload inventory
        loadInventory(1, {}, rowsPerPage);
      } else {
        alert(`Import failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error.response?.data?.error || error.message}`);
    }
  };

  // Load data when component mounts or location changes
  useEffect(() => {
    if (selectedLocation?._id) {
      loadInventory(1, {}, rowsPerPage); // Pass rowsPerPage to ensure proper pagination
    }
  }, [selectedLocation?._id, rowsPerPage]);

  // Load inventory when filters change
  useEffect(() => {
    if (selectedLocation?._id) {
      const filters = {
        search: searchTerm,
        category: categoryFilter,
        status: statusFilter
      };
      setPage(0); // Reset to first page when filters change
      loadInventory(1, filters, rowsPerPage);
    }
  }, [searchTerm, categoryFilter, statusFilter, selectedLocation?._id]);



  // Simplified handlers for the refactored components
  const handleViewItem = (item) => {
    setSelectedItem(item);
    setDetailsOpen(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setEditOpen(true);
  };

  const handleDeleteItem = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem && selectedLocation) {
      const itemId = selectedItem.id || selectedItem._id;
      console.log('Deleting item:', itemId);
      
      try {
        setIsSubmitting(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5006/api'}/inventory/${itemId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const result = await response.json();
        
        if (result.success) {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
          loadInventory(); // Refresh the inventory list
          alert('Item deleted successfully');
        } else {
          throw new Error(result.message || 'Failed to delete item');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (selectedLocation?._id) {
      loadInventory(newPage + 1, {}, rowsPerPage); // Convert to 1-based for API
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (selectedLocation?._id) {
      loadInventory(1, undefined, newRowsPerPage);
    }
  };

  const handleAddItemClose = () => {
    setAddOpen(false);
  };

  const handleAddItemSubmit = async (itemData, images) => {
    if (!selectedLocation) {
      alert('Please select a location first.');
      return;
    }

    try {
      await addItem(itemData, images);
      setAddOpen(false);
      // Refresh the inventory list
      loadInventory(selectedLocation.id, page, rowsPerPage, searchQuery, categoryFilter, statusFilter);
    } catch (err) {
      console.error('Error adding item:', err);
    }
  };

  const handleEditItemSubmit = async (itemData, newImages, itemId) => {
    try {
      // Add new images to itemData
      const itemDataWithImages = {
        ...itemData,
        images: newImages
      };
      
      await updateItem(itemId, itemDataWithImages);
      setEditOpen(false);
      // Refresh the inventory list
      loadInventory(selectedLocation.id, page, rowsPerPage, searchQuery, categoryFilter, statusFilter);
    } catch (err) {
      console.error('Error updating item:', err);
    }
  };

  const handleAiAddOpen = () => {
    setAiAddOpen(true);
  };

  const handleAiAddClose = () => {
    setAiAddOpen(false);
  };

  const handleAiAddItemSubmit = async (formData) => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5006/api'}/ai-detection/add-detected-item`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
          // Don't set Content-Type for FormData - browser sets it automatically with boundary
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        setAiAddOpen(false);
        loadInventory();
        alert(`Item ${result.data.action} successfully!`);
      } else {
        throw new Error(result.message || 'Failed to add item');
      }
    } catch (error) {
      console.error('Error submitting AI detected item:', error);
      alert('Failed to add item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 4,
          gap: 2,
          flexDirection: { xs: 'column', md: 'row' }
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Inventory Management
          </Typography>
          {!selectedLocation && (
            <Typography variant="subtitle1" color="text.secondary">
              Please select a location to manage inventory
            </Typography>
          )}
          {selectedLocation && (
            <Typography variant="subtitle1" color="text.secondary">
              Managing inventory for: {selectedLocation.name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
          <ImportExportButtons
            onExport={handleExport}
            onImport={handleImport}
            disabled={!selectedLocation}
            loading={loading}
          />
          <Button
            variant="outlined"
            startIcon={<CameraAltIcon />}
            size="large"
            sx={{ fontWeight: 'bold', width: { xs: '100%', sm: 'auto' } }}
            onClick={handleAiAddOpen}
            disabled={!selectedLocation}
          >
            AI Add Item
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            sx={{ fontWeight: 'bold', width: { xs: '100%', sm: 'auto' } }}
            onClick={() => setAddOpen(true)}
            disabled={!selectedLocation}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <InventoryStats stats={stats} loading={loading} />

      {/* Low Stock Alert */}
      {stats?.lowStockItems > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small">
              View Details
            </Button>
          }
        >
          {stats.lowStockItems} items are running low on stock and need restocking.
        </Alert>
      )}

      {/* Filters */}
      <InventoryFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      {/* Inventory Table */}
      <InventoryTable
        inventory={inventory}
        loading={loading}
        error={error}
        selectedLocation={selectedLocation}
        totalCount={pagination?.total || pagination?.totalItems || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onViewItem={handleViewItem}
        onEditItem={handleEditItem}
        onDeleteItem={handleDeleteItem}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} fullScreen={isMobile}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedItem?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={handleDeleteCancel} sx={{ width: { xs: '100%', sm: 'auto' } }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
              {selectedItem?.name}
            </Typography>
            <Chip
              label={getStatusText(selectedItem)}
              color={getStatusColor(selectedItem)}
              variant="filled"
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {!selectedItem ? (
            <Typography>No item selected</Typography>
          ) : (
            <Grid container spacing={4}>
              {/* Image Gallery Section */}
              <Grid item xs={12} md={5}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Product Images
                  </Typography>
                  {(() => {
                    // Parse images if they're stored as JSON string
                    let allImages = selectedItem.images;
                    if (typeof allImages === 'string') {
                      try {
                        allImages = JSON.parse(allImages);
                      } catch (e) {
                        console.log('Failed to parse images JSON:', e);
                        allImages = [];
                      }
                    }
                    
                    // Ensure images is an array
                    if (!Array.isArray(allImages)) {
                      allImages = [];
                    }

                    // Filter product images (exclude warehouse location images)
                    const productImages = allImages.filter(img => 
                      !img.type || img.type !== 'Warehouse Location'
                    );

                    const getImageUrl = (image) => {
                      const imageUrl = image?.url || image || image?.secure_url;
                      if (imageUrl && imageUrl.startsWith('/uploads/')) {
                        const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5006').replace('/api', '');
                        return `${serverUrl}${imageUrl}`;
                      }
                      return imageUrl;
                    };

                    if (productImages && productImages.length > 0) {
                      return (
                        <Box>
                          {/* Main Image */}
                          <Box
                            component="img"
                            src={getImageUrl(productImages[selectedImageIndex])}
                            alt={selectedItem.name}
                            sx={{
                              width: '100%',
                              height: 300,
                              objectFit: 'cover',
                              borderRadius: 2,
                              mb: 2,
                              boxShadow: 2
                            }}
                            onError={(e) => {
                              console.log('Image failed to load:', productImages[selectedImageIndex]);
                              e.target.style.display = 'none';
                            }}
                          />
                          
                          {/* Image Thumbnails */}
                          {productImages.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {productImages.map((image, index) => (
                                <Box
                                  key={index}
                                  component="img"
                                  src={getImageUrl(image)}
                                  alt={`${selectedItem.name} ${index + 1}`}
                                  onClick={() => setSelectedImageIndex(index)}
                                  sx={{
                                    width: 80,
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    border: selectedImageIndex === index ? '2px solid primary.main' : '2px solid transparent',
                                    '&:hover': {
                                      border: '2px solid primary.main'
                                    }
                                  }}
                                  onError={(e) => {
                                    console.log('Thumbnail failed to load:', image);
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    } else {
                      return (
                        <Box
                          sx={{
                            width: '100%',
                            height: 300,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100',
                            borderRadius: 2,
                            border: '2px dashed',
                            borderColor: 'grey.300'
                          }}
                        >
                          <Typography color="text.secondary">
                            No images available
                          </Typography>
                        </Box>
                      );
                    }
                  })()}

                {/* Warehouse Location Photos */}
                {(() => {
                  // Parse images if they're stored as JSON string (same as above)
                  let allImages = selectedItem.images;
                  if (typeof allImages === 'string') {
                    try {
                      allImages = JSON.parse(allImages);
                    } catch (e) {
                      console.log('Failed to parse images JSON:', e);
                      allImages = [];
                    }
                  }
                  
                  // Ensure images is an array
                  if (!Array.isArray(allImages)) {
                    allImages = [];
                  }

                  // Filter warehouse location images - check multiple possible formats
                  const warehouseImages = allImages.filter(img => 
                    img.type === 'Warehouse Location' || 
                    (typeof img === 'string' && img.includes('warehouse-location')) ||
                    (img.alt && img.alt.toLowerCase().includes('warehouse')) ||
                    (img.filename && img.filename.includes('warehouse-location'))
                  );

                  const getImageUrl = (image) => {
                    const imageUrl = image?.url || image || image?.secure_url;
                    if (imageUrl && imageUrl.startsWith('/uploads/')) {
                      const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5006').replace('/api', '');
                      return `${serverUrl}${imageUrl}`;
                    }
                    return imageUrl;
                  };

                  if (warehouseImages && warehouseImages.length > 0) {
                    return (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Warehouse Location Photos
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {warehouseImages.map((image, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: 'relative',
                                width: 120,
                                height: 120,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  boxShadow: 3,
                                  transform: 'scale(1.02)'
                                },
                                transition: 'all 0.2s'
                              }}
                              onClick={() => {
                                setSelectedModalImage(image);
                                setImageModalOpen(true);
                              }}
                            >
                              <Box
                                component="img"
                                src={getImageUrl(image)}
                                alt={image.alt || `Warehouse location ${index + 1}`}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  console.log('Warehouse image failed to load:', image);
                                  e.target.style.display = 'none';
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  bgcolor: 'rgba(0,0,0,0.7)',
                                  color: 'white',
                                  p: 0.5,
                                  fontSize: '0.7rem',
                                  textAlign: 'center'
                                }}
                              >
                                {image.alt?.includes('Shipping Label') ? '📦 Label' : '📍 Location'}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })()}
                </Box>
              </Grid>

              {/* Product Details Section */}
              <Grid item xs={12} md={7}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Product Information
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        SKU
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {selectedItem.sku}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Item ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedItem.id || selectedItem._id}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedItem.mainCategory || selectedItem.category?.main || 'N/A'}
                      {(selectedItem.subCategory || selectedItem.category?.sub) && 
                        ` / ${selectedItem.subCategory || selectedItem.category.sub}`}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedItem.description || 'No description available'}
                    </Typography>
                  </Box>
                </Box>

                {/* Pricing Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Pricing
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Retail Price
                      </Typography>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                        ${(selectedItem.retail || selectedItem.pricing?.retail || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Cost Price
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        ${(selectedItem.cost || selectedItem.pricing?.cost || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Stock Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Stock Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Current Stock
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {selectedItem.quantity || selectedItem.stock?.available || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Reorder Point
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {selectedItem.reorderPoint || selectedItem.stock?.reorderPoint || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Max Stock
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {selectedItem.maxQuantity || selectedItem.stock?.maxQuantity || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Warehouse Location */}
                {(selectedItem.zone || selectedItem.aisle || selectedItem.shelf || selectedItem.bin || 
                  selectedItem.positionX || selectedItem.positionY || selectedItem.positionZ) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Warehouse Location
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedItem.zone && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Zone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {selectedItem.zone}
                          </Typography>
                        </Grid>
                      )}
                      {selectedItem.aisle && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Aisle
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {selectedItem.aisle}
                          </Typography>
                        </Grid>
                      )}
                      {selectedItem.shelf && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Shelf
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {selectedItem.shelf}
                          </Typography>
                        </Grid>
                      )}
                      {selectedItem.bin && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Bin
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {selectedItem.bin}
                          </Typography>
                        </Grid>
                      )}
                      {(selectedItem.positionX || selectedItem.positionY || selectedItem.positionZ) && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">
                            3D Position (X, Y, Z)
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            ({selectedItem.positionX || 0}, {selectedItem.positionY || 0}, {selectedItem.positionZ || 0})
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Physical Dimensions */}
                {(selectedItem.boxLength || selectedItem.boxWidth || selectedItem.boxHeight) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Physical Dimensions
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Length
                        </Typography>
                        <Typography variant="body1">
                          {selectedItem.boxLength ? `${selectedItem.boxLength}"` : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Width
                        </Typography>
                        <Typography variant="body1">
                          {selectedItem.boxWidth ? `${selectedItem.boxWidth}"` : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Height
                        </Typography>
                        <Typography variant="body1">
                          {selectedItem.boxHeight ? `${selectedItem.boxHeight}"` : 'N/A'}
                        </Typography>
                      </Grid>
                      {selectedItem.boxVolume && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Volume
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.boxVolume} cubic inches
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Supplier Information */}
                {selectedItem.supplier && typeof selectedItem.supplier === 'object' && Object.keys(selectedItem.supplier).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Supplier Information
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedItem.supplier.name && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Supplier Name
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.supplier.name}
                          </Typography>
                        </Grid>
                      )}
                      {selectedItem.supplier.contact && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Contact
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.supplier.contact}
                          </Typography>
                        </Grid>
                      )}
                      {selectedItem.supplier.email && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.supplier.email}
                          </Typography>
                        </Grid>
                      )}
                      {selectedItem.supplier.phone && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {selectedItem.supplier.phone}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* AI Detection Information */}
                {selectedItem.aiDetection && typeof selectedItem.aiDetection === 'object' && selectedItem.aiDetection.isAiGenerated && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      AI Detection Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Confidence Score
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {Math.round((selectedItem.aiDetection.confidence || 0) * 100)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Detected At
                        </Typography>
                        <Typography variant="body1">
                          {selectedItem.aiDetection.detectedAt ? 
                            new Date(selectedItem.aiDetection.detectedAt).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Manually Verified
                        </Typography>
                        <Chip 
                          label={selectedItem.aiDetection.manuallyVerified ? 'Yes' : 'No'}
                          color={selectedItem.aiDetection.manuallyVerified ? 'success' : 'warning'}
                          size="small"
                        />
                      </Grid>
                      {selectedItem.productUrl && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Product URL
                          </Typography>
                          <Typography variant="body1">
                            <a 
                              href={selectedItem.productUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#1976d2', textDecoration: 'none' }}
                            >
                              🔗 View Original Product
                            </a>
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Analytics */}
                {(selectedItem.views > 0 || selectedItem.orders > 0 || selectedItem.revenue > 0) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Analytics
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Views
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {selectedItem.views || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Orders
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {selectedItem.orders || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Revenue
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          ${(selectedItem.revenue || 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      {selectedItem.lastOrdered && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Last Ordered
                          </Typography>
                          <Typography variant="body1">
                            {new Date(selectedItem.lastOrdered).toLocaleDateString()}
                          </Typography>
                        </Grid>
                      )}
                      {selectedItem.lastStockUpdate && (
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Last Stock Update
                          </Typography>
                          <Typography variant="body1">
                            {new Date(selectedItem.lastStockUpdate).toLocaleDateString()}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* Additional Information */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Brand
                      </Typography>
                      <Typography variant="body1">
                        {selectedItem.brand || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Model
                      </Typography>
                      <Typography variant="body1">
                        {selectedItem.model || 'Not specified'}
                      </Typography>
                    </Grid>
                    {selectedItem.barcode && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Barcode
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                          {selectedItem.barcode}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip 
                        label={selectedItem.status || 'ACTIVE'}
                        color={selectedItem.status === 'ACTIVE' ? 'success' : 'default'}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1">
                        {selectedLocation?.name || 'No location selected'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body1">
                        {new Date(selectedItem.updatedAt || selectedItem.createdAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    {selectedItem.tags && selectedItem.tags.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedItem.tags.map((tag, index) => (
                            <Chip key={index} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setDetailsOpen(false)} size="large" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Close
          </Button>
          <Button 
            variant="contained" 
            size="large"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            onClick={() => {
              setDetailsOpen(false);
              handleEditItem(selectedItem);
            }}
          >
            Edit Item
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            color="success"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Restock Item
          </Button>
        </DialogActions>
      </Dialog>

        <EditItemModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSubmit={handleEditItemSubmit}
          item={selectedItem}
          selectedLocation={selectedLocation}
        />

      {/* Add Item Modal */}
      <AddItemModal
        open={addOpen}
        onClose={handleAddItemClose}
        onSubmit={handleAddItemSubmit}
        selectedLocation={selectedLocation}
      />

      {/* AI Add Item Modal */}
      <AiAddItemModal
        open={aiAddOpen}
        onClose={handleAiAddClose}
        onSubmit={handleAiAddItemSubmit}
        selectedLocation={selectedLocation}
      />

      {/* Import Results Modal */}
      <Dialog
        open={importResultsOpen}
        onClose={() => setImportResultsOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Import Results
          <IconButton onClick={() => setImportResultsOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {importResults && (
            <Box>
              {/* Summary */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Chip 
                  label={`${importResults.summary?.created || 0} Created`} 
                  color="success" 
                  variant="filled"
                />
                <Chip 
                  label={`${importResults.summary?.updated || 0} Updated`} 
                  color="info" 
                  variant="filled"
                />
                <Chip 
                  label={`${importResults.summary?.unchanged || 0} Unchanged`} 
                  color="default" 
                  variant="outlined"
                />
                {(importResults.summary?.errors || 0) > 0 && (
                  <Chip 
                    label={`${importResults.summary?.errors} Errors`} 
                    color="error" 
                    variant="filled"
                  />
                )}
              </Box>

              {/* Updated Items with Changes */}
              {importResults.updated?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'info.main' }}>
                    Updated Items ({importResults.updated.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>SKU</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Changes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResults.updated.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {item.sku}
                            </TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>
                              {item.changes?.map((change, cIdx) => (
                                <Box key={cIdx} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" component="span" sx={{ fontWeight: 'bold' }}>
                                    {change.field}:
                                  </Typography>{' '}
                                  <Typography variant="caption" component="span" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
                                    {change.from ?? 'empty'}
                                  </Typography>
                                  {' → '}
                                  <Typography variant="caption" component="span" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                    {change.to ?? 'empty'}
                                  </Typography>
                                </Box>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Created Items */}
              {importResults.created?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
                    New Items Created ({importResults.created.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>SKU</TableCell>
                          <TableCell>Name</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResults.created.slice(0, 20).map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {item.sku}
                            </TableCell>
                            <TableCell>{item.name}</TableCell>
                          </TableRow>
                        ))}
                        {importResults.created.length > 20 && (
                          <TableRow>
                            <TableCell colSpan={2} sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                              ... and {importResults.created.length - 20} more
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Errors */}
              {importResults.errors?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
                    Errors ({importResults.errors.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResults.errors.map((err, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{err.row}</TableCell>
                            <TableCell sx={{ color: 'error.main' }}>{err.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Unchanged Items (collapsed by default) */}
              {importResults.unchanged?.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {importResults.unchanged.length} items were unchanged (no differences detected)
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResultsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal for Warehouse Photos */}
      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={imageModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 2,
              outline: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => setImageModalOpen(false)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Image */}
            {selectedModalImage && (
              <>
                <Box
                  component="img"
                  src={(() => {
                    const imageUrl = selectedModalImage?.url || selectedModalImage || selectedModalImage?.secure_url;
                    if (imageUrl && imageUrl.startsWith('/uploads/')) {
                      const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5006').replace('/api', '');
                      return `${serverUrl}${imageUrl}`;
                    }
                    return imageUrl;
                  })()}
                  alt={selectedModalImage.alt || 'Warehouse location photo'}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: 1
                  }}
                  onError={(e) => {
                    console.log('Modal image failed to load:', selectedModalImage);
                  }}
                />
                
                {/* Image Caption */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 2, 
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontWeight: 'bold'
                  }}
                >
                  {selectedModalImage.alt?.includes('Shipping Label') ? '📦 Shipping Label' : '📍 Warehouse Location Photo'}
                </Typography>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Inventory;
